/** Découpage d'un ralenti en messages `replayChunk` (DataChannel) et réassemblage. */
import type { Message } from '../game/protocol';
import { MAX_CLIP_BYTES } from './clipCodec';

export type ReplayChunk = Extract<Message, { type: 'replayChunk' }>;

const toBase64 = (bytes: Uint8Array): string => {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
};

const fromBase64 = (text: string): Uint8Array =>
  Uint8Array.from(atob(text), (c) => c.charCodeAt(0));

export function toChunks(bytes: Uint8Array, round: number, chunkBytes = 12_000): ReplayChunk[] {
  const total = Math.max(1, Math.ceil(bytes.byteLength / chunkBytes));
  return Array.from({ length: total }, (_, seq) => ({
    type: 'replayChunk',
    round,
    seq,
    total,
    data: toBase64(bytes.subarray(seq * chunkBytes, (seq + 1) * chunkBytes)),
  }));
}

export interface ClipAssembler {
  /** Renvoie les octets complets quand le dernier morceau manquant arrive. */
  push(chunk: ReplayChunk): Uint8Array | null;
  progress(): number;
  failed(): boolean;
}

export function createClipAssembler(round: number, maxBytes = MAX_CLIP_BYTES): ClipAssembler {
  let parts = new Map<number, Uint8Array>();
  let total = 0;
  let size = 0;
  let done = false;
  let broken = false;
  return {
    push(chunk) {
      if (done || broken || chunk.round !== round) return null;
      if (total !== chunk.total) {
        // Incohérent avec les morceaux déjà reçus : on repart de zéro.
        parts = new Map();
        total = chunk.total;
        size = 0;
      }
      if (parts.has(chunk.seq)) return null;
      const bytes = fromBase64(chunk.data);
      size += bytes.byteLength;
      if (size > maxBytes) {
        broken = true;
        parts.clear();
        return null;
      }
      parts.set(chunk.seq, bytes);
      if (parts.size < total) return null;
      done = true;
      const out = new Uint8Array(size);
      let offset = 0;
      for (let seq = 0; seq < total; seq++) {
        const part = parts.get(seq) ?? new Uint8Array(0);
        out.set(part, offset);
        offset += part.byteLength;
      }
      return out;
    },
    progress: () => (done ? 1 : total > 0 ? parts.size / total : 0),
    failed: () => broken,
  };
}
